import { type ReactNode, useEffect } from 'react'
import {
  PreferencesProvider,
  localePref,
  usePreference,
  usePreferencesReady,
} from '@rammetec/app-kit/preferences'
import { useCurrentUser, useSessionManager } from '@syscolect/application'
import i18n from '@/services/i18n.bootstrap'
import { preferencesService } from '@/services/preferences.bootstrap'

/**
 * Applies persisted locale after hydrate. Detector still covers pre-login.
 */
function LocalePreferenceBridge() {
  const [locale] = usePreference(localePref)
  const ready = usePreferencesReady()

  useEffect(() => {
    if (!ready || !locale) return
    if (i18n.language === locale) return
    void i18n.changeLanguage(locale)
  }, [ready, locale])

  return null
}

/**
 * Mounts `PreferencesProvider` only when a user is present so hydrate / persist
 * run, and `service.reset()` fires on logout (provider unmount).
 *
 * `currentUserId` comes from `useCurrentUser` — `useSessionManager` does not
 * expose it.
 */
export function PreferencesBridge({ children }: { children: ReactNode }) {
  const { currentUserId } = useCurrentUser()
  const { currentOrganizationId } = useSessionManager()

  if (!currentUserId) {
    return children
  }

  return (
    <PreferencesProvider
      service={preferencesService}
      product="sc"
      userId={currentUserId}
      orgId={currentOrganizationId ?? undefined}
    >
      <LocalePreferenceBridge />
      {children}
    </PreferencesProvider>
  )
}
